import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import {
  canView,
  getAccess,
  getFirstVisiblePagePath,
  pageKeyFromPath,
} from '@/utils/pagePermissions'

function ReadOnlyBanner() {
  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
      You have read-only access to this page.
    </div>
  )
}

function NoAccessMessage() {
  return (
    <div className="flex h-full min-h-[12rem] items-center justify-center p-6">
      <div className="max-w-md rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
        <h2 className="text-base font-semibold text-gray-900">No page access</h2>
        <p className="mt-2 text-sm text-gray-600">
          Your account does not have access to any pages in BookKeeper. Contact an admin if you
          believe this is a mistake.
        </p>
      </div>
    </div>
  )
}

export default function RequirePageAccess({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const location = useLocation()
  const pageKey = pageKeyFromPath(location.pathname)

  if (!pageKey) {
    return <>{children}</>
  }

  const access = user ? getAccess(user, pageKey) : 'hidden'

  if (!canView(user, pageKey)) {
    const fallbackPath = getFirstVisiblePagePath(user)
    if (!fallbackPath) {
      return <NoAccessMessage />
    }
    if (location.pathname !== fallbackPath) {
      return <Navigate to={fallbackPath} replace />
    }
    return <NoAccessMessage />
  }

  if (access === 'read') {
    const suppressReadOnlyBanner = location.pathname.startsWith('/reporting/members')
    if (suppressReadOnlyBanner) {
      return <>{children}</>
    }
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <ReadOnlyBanner />
        <div className="flex-1 min-h-0 overflow-auto" data-readonly>
          {children}
        </div>
      </div>
    )
  }

  return <>{children}</>
}
